/**
 * CourseMaterialForm — add an external link material (not file upload).
 * For file uploads use CourseMaterialUploadModal instead.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const LINK_TYPES = [
  { value: 'link', label: 'Liên kết' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: 'slide', label: 'Slide' },
  { value: 'document', label: 'Tài liệu' },
] as const;

const materialLinkFormSchema = z.object({
  type: z.enum(['link', 'video', 'audio', 'slide', 'document']),
  title: z.string().min(1, 'Bắt buộc').max(300),
  url: z.string().url('URL không hợp lệ'),
  description: z.string().optional().or(z.literal('')),
});

export type MaterialLinkFormValues = z.infer<typeof materialLinkFormSchema>;

interface CourseMaterialFormProps {
  defaultValues?: Partial<MaterialLinkFormValues>;
  isSubmitting?: boolean;
  onSubmit: (values: MaterialLinkFormValues) => void;
  onCancel: () => void;
}

export function CourseMaterialForm({
  defaultValues,
  isSubmitting,
  onSubmit,
  onCancel,
}: CourseMaterialFormProps) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<MaterialLinkFormValues>({
    resolver: zodResolver(materialLinkFormSchema),
    defaultValues: {
      type: 'link',
      title: '',
      url: '',
      description: '',
      ...defaultValues,
    },
  });

  useEffect(() => {
    reset({ type: 'link', title: '', url: '', description: '', ...defaultValues });
  }, [defaultValues, reset]);

  const typeVal = watch('type');

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label className="text-sm font-medium">
          Loại học liệu <span className="text-destructive">*</span>
        </Label>
        <Select
          value={typeVal}
          onValueChange={(v) => setValue('type', v as MaterialLinkFormValues['type'])}
        >
          <SelectTrigger className="h-10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LINK_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="mat-title" className="text-sm font-medium">
          Tiêu đề <span className="text-destructive">*</span>
        </Label>
        <Input id="mat-title" className="h-10" placeholder="Tên học liệu..." {...register('title')} />
        {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="mat-url" className="text-sm font-medium">
          URL <span className="text-destructive">*</span>
        </Label>
        <Input
          id="mat-url"
          type="url"
          className="h-10"
          placeholder="https://..."
          {...register('url')}
        />
        {errors.url && <p className="text-xs text-destructive">{errors.url.message}</p>}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="mat-description" className="text-sm font-medium">Mô tả</Label>
        <Textarea id="mat-description" rows={2} placeholder="Mô tả ngắn..." {...register('description')} />
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          Hủy
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Thêm liên kết'}
        </Button>
      </div>
    </form>
  );
}
